import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@assets/environment/environment';
import { ENDPOINTS } from '@app/constants/config.enum';
import { HelperService } from '@app/services/helper.service';
import { ValidateAccessCodeResponse } from '@app/shared/models/validate-access-code';
import {
  StripePlan,
  StripePricingResponse,
} from '@app/shared/models/stripe-pricing';
import { StripeCouponResponse } from '@app/shared/models/stripe-coupon';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private token: string = '';
  private clientSecret: string = '';
  private selectedPrice: string = '';
  selectedPlan: WritableSignal<StripePlan> = signal({} as StripePlan);

  constructor(private http: HttpClient, private helper: HelperService) {}

  /**
   * @description This method is used to create a subscription.
   * @param params priceId, customerId, and optional couponCode.
   * @returns clientSecret.
   */
  createStripeSubscription(params: {
    priceId: string;
    customerId: string;
    couponCode?: string;
    requirePaymentMethod?: boolean;
  }): Observable<any> {
    return this.http.post<HttpResponse<any>>(
      `${environment.baseUrl + ENDPOINTS.CREATE_STRIPE_CHECKOUT}`,
      params
    );
  }

  applyCode(params: any): Observable<any> {
    return this.http.post<HttpResponse<any>>(
      `${environment.baseUrl + ENDPOINTS.APPLY_PROMO_CODE}`,
      params
    );
  }

  validateCode(code: string): Observable<ValidateAccessCodeResponse> {
    return this.http.get<ValidateAccessCodeResponse>(
      `${environment.baseUrl + ENDPOINTS.VALIDATE_PROMO_CODE}`,
      { params: { access_code: code } }
    );
  }

  validateSubscription(
    params: { sid: string },
    token: string
  ): Observable<any> {
    const options = this.helper.getHeaderOptions(token);
    return this.http.post<HttpResponse<any>>(
      `${environment.baseUrl + ENDPOINTS.VALIDATE_SUBSCRIPTION}`,
      params,
      options
    );
  }

  getAvailablePrices(): Observable<StripePricingResponse> {
    return this.http.get<StripePricingResponse>(
      `${environment.baseUrl + ENDPOINTS.GET_AVAILABLE_PLANS}`
    );
  }

  createStripeCustomer(params: {
    email: string;
    firstName: string;
  }): Observable<any> {
    return this.http.post<HttpResponse<any>>(
      `${environment.baseUrl + ENDPOINTS.CREATE_STRIPE_CUSTOMER}`,
      params
    );
  }

  /**
   * @description This method is used to get coupon information by coupon code.
   * @param couponCode The Stripe coupon code.
   * @returns Coupon information including name and discount details.
   */
  getCouponInfo(couponCode: string): Observable<StripeCouponResponse> {
    return this.http.get<StripeCouponResponse>(
      `${environment.baseUrl + ENDPOINTS.GET_COUPON_INFO}`,
      { params: { coupon_code: couponCode } }
    );
  }

  setToken(token: string) {
    this.token = token;
  }

  getToken(): string {
    return this.token;
  }
}
